import React, { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FixedSizeList } from 'react-window'
import { useTranslation } from 'contexts/Localization'
import { Currency, Token } from '@nominex/nomiswap-sdk'
import { SlInput } from '@shoelace-style/shoelace/dist/react'
import { useAudioModeManager } from 'state/user/hooks'
import useDebounce from 'hooks/useDebounce'
import { useWagmi } from 'libs/wagmi'
import { useAllTokens, useIsUserAddedToken, useToken } from 'hooks/Tokens'
import useNativeCurrency from 'hooks/useNativeCurrency'
import { useAllLists, useInactiveListUrls, WrappedTokenInfo } from 'state/lists/hooks'
import { isAddress } from 'utils'
import CommonBases from './CommonBases'
import CurrencyList from './CurrencyList'
import { createFilterToken, useSortedTokensByQuery } from './filtering'
import useTokenComparator from './sorting'
import { getSwapSound } from './swapSound'
import ImportRow from './ImportRow'

interface CurrencySearchProps {
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  showCommonBases?: boolean
  showSearchControl?: boolean
  allowedTokens?: string[]
  unallowedTokens?: string[]
  showImportView: () => void
  setImportToken: (token: Token) => void
}

function useSearchInactiveTokenLists(search: string | undefined, minResults = 10): WrappedTokenInfo[] {
  const lists = useAllLists()
  const inactiveUrls = useInactiveListUrls()
  const { chainId } = useWagmi()
  const activeTokens = useAllTokens()

  return useMemo(() => {
    if (!search || search.trim().length === 0) {
      return []
    }

    const filterToken = createFilterToken(search)
    const exactMatches: WrappedTokenInfo[] = []
    const restMatches: WrappedTokenInfo[] = []
    const addressSet: { [address: string]: boolean } = {}
    const trimmedSearchQuery = search.toLowerCase().trim()

    const tokens = [].concat(...inactiveUrls.map((url) => lists[url]?.current?.tokens ?? []))

    tokens.forEach((tokenInfo) => {
      if (
        tokenInfo.chainId === chainId &&
        !(tokenInfo.address in activeTokens) &&
        !addressSet[tokenInfo.address] &&
        filterToken(tokenInfo)
      ) {
        const wrapped: WrappedTokenInfo = new WrappedTokenInfo(tokenInfo, [])

        addressSet[wrapped.address] = true

        if (
          tokenInfo.name?.toLowerCase() === trimmedSearchQuery ||
          tokenInfo.symbol?.toLowerCase() === trimmedSearchQuery
        ) {
          exactMatches.push(wrapped)
        } else {
          restMatches.push(wrapped)
        }
      }
    })
    return [...exactMatches, ...restMatches].slice(0, minResults)
  }, [activeTokens, chainId, inactiveUrls, lists, minResults, search])
}

function CurrencySearch({
  selectedCurrency,
  onCurrencySelect,
  showCommonBases,
  showSearchControl,
  showImportView,
  allowedTokens = [],
  unallowedTokens = [],
  setImportToken,
}: CurrencySearchProps) {
  const { t } = useTranslation()
  const { chainId } = useWagmi()
  const [audioPlay] = useAudioModeManager()
  const native = useNativeCurrency()

  // refs for fixed size lists
  const fixedList = useRef<FixedSizeList>()
  // manage focus on modal show
  const inputRef = useRef<any>()

  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedQuery = useDebounce(searchQuery, 750)

  const allTokens = useAllTokens()

  // if they input an address, use it
  const searchToken = useToken(debouncedQuery)
  const searchTokenIsAdded = useIsUserAddedToken(searchToken)

  const filteredTokens: Token[] = useMemo(() => {
    const filterToken = createFilterToken(debouncedQuery)
    return Object.values(allTokens).filter(filterToken)
  }, [allTokens, debouncedQuery])

  const filteredQueryTokens = useSortedTokensByQuery(filteredTokens, debouncedQuery)
  const tokenComparator = useTokenComparator(false)

  const filteredSortedTokens: Token[] = useMemo(
    () =>
      [...filteredQueryTokens]
        .sort(tokenComparator)
        .filter((token: Token) => (allowedTokens.length ? allowedTokens.includes(token?.address) : true))
        .filter((token: Token) => !unallowedTokens.includes(token?.address)),
    [allowedTokens, unallowedTokens, filteredQueryTokens, tokenComparator],
  )

  // if no results on main list, show option to expand into inactive
  const filteredInactiveTokens = useSearchInactiveTokenLists(debouncedQuery)

  const handleCurrencySelect = useCallback(
    (currency: Currency) => {
      onCurrencySelect(currency)
      if (audioPlay) {
        getSwapSound()?.play()
      }
    },
    [audioPlay, onCurrencySelect],
  )

  useEffect(() => {
    if (inputRef.current?.input) {
      inputRef.current?.focus()
    }
  }, [inputRef])

  const handleInput = useCallback((event) => {
    const input = event.target.value
    const checksummedInput = isAddress(input)
    setSearchQuery(checksummedInput || input)
    fixedList.current?.scrollTo(0)
  }, [])

  const handleEnter = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const s = debouncedQuery.toLowerCase().trim()

        if (s === native.symbol.toLowerCase().trim()) {
          handleCurrencySelect(native)
        } else if (filteredSortedTokens.length > 0) {
          if (
            filteredSortedTokens[0].symbol?.toLowerCase() === debouncedQuery.trim().toLowerCase() ||
            filteredSortedTokens.length === 1
          ) {
            handleCurrencySelect(filteredSortedTokens[0])
          }
        }
      }
    },
    [filteredSortedTokens, handleCurrencySelect, debouncedQuery, native],
  )

  const getCurrencyListRows = useCallback(
    () =>
      filteredInactiveTokens?.length || filteredSortedTokens?.length ? (
        <CurrencyList
          height={336}
          currencies={filteredSortedTokens}
          inactiveCurrencies={filteredInactiveTokens}
          onCurrencySelect={handleCurrencySelect}
          selectedCurrency={selectedCurrency}
          fixedListRef={fixedList}
          showImportView={showImportView}
          setImportToken={setImportToken}
        />
      ) : (
        <div className="text-center p-24 h-full">
          <p className="text-16 text-white font-medium">{t('No results found')}</p>
        </div>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredInactiveTokens, filteredSortedTokens, selectedCurrency],
  )

  return (
    <>
      {(showSearchControl || showCommonBases) && (
        <div className="flex flex-col gap-16 px-24 pb-24">
          {showSearchControl && (
            <SlInput
              id="token-search-input"
              placeholder={t('Search name or paste address')}
              autocomplete="off"
              value={searchQuery}
              ref={inputRef}
              onSlInput={handleInput}
              onKeyDown={handleEnter}
            />
          )}
          {showCommonBases && (
            <CommonBases
              allowedTokens={allowedTokens}
              unallowedTokens={unallowedTokens}
              chainId={chainId}
              onSelect={handleCurrencySelect}
              selectedCurrency={selectedCurrency}
            />
          )}
        </div>
      )}

      {searchToken && !searchTokenIsAdded ? (
        <ImportRow token={searchToken} showImportView={showImportView} setImportToken={setImportToken} />
      ) : (
        getCurrencyListRows()
      )}
    </>
  )
}

export default CurrencySearch
